import { useState } from "react";

import { DoctorStatusBadge } from "@/components/common/status-badge";
import { DoctorForm } from "@/components/doctor/doctor-form";
import { RequireDoctor } from "@/components/doctor/require-doctor";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { formatCurrency, initials } from "@/lib/format";
import type { Doctor } from "@/types/doctor";
import { usePageMeta } from "@/lib/page-meta";

export function ProfileRoute() {
  usePageMeta({
    title: "Profile — HealNova Doctor Portal",
    description: "Review and update your HealNova practice profile, fees and weekly availability.",
  });
  return (
    <RequireDoctor allowUnapproved>{(doctor) => <ProfilePage doctor={doctor} />}</RequireDoctor>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-1.5 text-sm font-medium break-words">{value || "—"}</dd>
    </div>
  );
}

function ProfilePage({ doctor }: { doctor: Doctor }) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <div className="mx-auto w-full max-w-3xl space-y-7">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-rose uppercase">Profile</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">
              Edit your details
            </h1>
          </div>
          <Button variant="outline" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </header>

        <DoctorForm mode="edit" doctor={doctor} onSuccess={() => setEditing(false)} />
      </div>
    );
  }

  const availableDays = doctor.schedule.filter((slot) => slot.isAvailable);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <section className="card-surface p-6 sm:p-8">
        <div className="flex flex-wrap items-start gap-5">
          <Avatar className="size-20 border border-border">
            {doctor.profilePicture && <AvatarImage src={doctor.profilePicture} alt={doctor.name} />}
            <AvatarFallback className="bg-primary text-xl font-semibold text-primary-foreground">
              {initials(doctor.name)}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">
                {doctor.name}
              </h1>
              <DoctorStatusBadge status={doctor.status} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {doctor.specialization} · {doctor.experience} years of experience
            </p>
            {doctor.bio && (
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {doctor.bio}
              </p>
            )}
          </div>

          <Button onClick={() => setEditing(true)}>Edit profile</Button>
        </div>

        <dl className="mt-8 grid gap-6 border-t border-border pt-8 sm:grid-cols-2 lg:grid-cols-3">
          <Detail label="Consultation fee" value={formatCurrency(doctor.fees)} />
          <Detail label="Location" value={doctor.location} />
          <Detail label="Hospital / clinic" value={doctor.hospitalName ?? ""} />
          <Detail label="Phone" value={doctor.phone ?? ""} />
          <Detail label="Email" value={doctor.email ?? ""} />
          <Detail
            label="Video consultations"
            value={doctor.videoConsultationAvailable ? "Enabled" : "Not offered"}
          />
          <Detail label="Qualifications" value={doctor.qualifications.join(", ")} />
          <Detail label="Languages" value={doctor.languages.join(", ")} />
          <Detail label="Rating" value={doctor.rating ? `${doctor.rating.toFixed(1)} / 5` : "—"} />
        </dl>
      </section>

      <section className="card-surface p-6 sm:p-8">
        <h2 className="font-display text-lg font-semibold">Weekly availability</h2>
        {availableDays.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            No consulting days set yet. Add them from Edit profile.
          </p>
        ) : (
          <ul className="mt-5 grid gap-2 sm:grid-cols-2">
            {availableDays.map((slot) => (
              <li
                key={slot.day}
                className="flex items-center justify-between rounded-xl border border-border px-4 py-3 text-sm"
              >
                <span className="font-medium">{slot.day}</span>
                <span className="text-muted-foreground tabular-nums">
                  {slot.startTime} – {slot.endTime}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
